import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider } from "@/lib/auth";
import PublicLayout from "@/components/public/PublicLayout";
import AdminLayout from "@/components/admin/AdminLayout";
import "@/App.css";

// Public pages
import HomePage from "@/pages/public/HomePage";
import JournalListPage from "@/pages/public/JournalListPage";
import ArticleDetailPage from "@/pages/public/ArticleDetailPage";
import HotelLandingPage from "@/pages/public/HotelLandingPage";
import KurucuMisafirPage from "@/pages/public/KurucuMisafirPage";
import ContactPage from "@/pages/public/ContactPage";
import FAQPage from "@/pages/public/FAQPage";
import SearchPage from "@/pages/public/SearchPage";
import SitemapPage from "@/pages/public/SitemapPage";
import LegalPage from "@/pages/public/LegalPage";
import NotFoundPage from "@/pages/public/NotFoundPage";
import AnsiklopediHubPage from "@/pages/public/AnsiklopediHubPage";
import KoleksiyonlarListPage, { KoleksiyonDetailPage } from "@/pages/public/KoleksiyonlarPage";
import KapadokyaTakvimiPage from "@/pages/public/KapadokyaTakvimiPage";
import CapellaStoriesPage from "@/pages/public/CapellaStoriesPage";

// Admin pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminArticlesPage from "@/pages/admin/AdminArticlesPage";
import AdminArticleEditorPage from "@/pages/admin/AdminArticleEditorPage";
import AdminCategoriesPage from "@/pages/admin/AdminCategoriesPage";
import AdminTagsPage from "@/pages/admin/AdminTagsPage";
import AdminMediaPage from "@/pages/admin/AdminMediaPage";
import AdminLeadsPage from "@/pages/admin/AdminLeadsPage";
import AdminSEOPage from "@/pages/admin/AdminSEOPage";
import AdminRedirectsPage from "@/pages/admin/AdminRedirectsPage";
import AdminMenusPage from "@/pages/admin/AdminMenusPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminCollectionsPage from "@/pages/admin/AdminCollectionsPage";
import AdminEventsPage from "@/pages/admin/AdminEventsPage";
import AdminStoriesPage from "@/pages/admin/AdminStoriesPage";
import AdminIdeasPage from "@/pages/admin/AdminIdeasPage";

const PublicShell = ({ children }) => <PublicLayout>{children}</PublicLayout>;
const AdminShell = ({ children }) => <AdminLayout>{children}</AdminLayout>;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Toaster position="top-right" />
          <Routes>
            {/* Admin login (no shell) */}
            <Route path="/admin/login" element={<AdminLoginPage />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminShell><AdminDashboardPage /></AdminShell>} />
            <Route path="/admin/articles" element={<AdminShell><AdminArticlesPage /></AdminShell>} />
            <Route path="/admin/articles/:id" element={<AdminShell><AdminArticleEditorPage /></AdminShell>} />
            <Route path="/admin/categories" element={<AdminShell><AdminCategoriesPage /></AdminShell>} />
            <Route path="/admin/tags" element={<AdminShell><AdminTagsPage /></AdminShell>} />
            <Route path="/admin/media" element={<AdminShell><AdminMediaPage /></AdminShell>} />
            <Route path="/admin/leads" element={<AdminShell><AdminLeadsPage /></AdminShell>} />
            <Route path="/admin/seo" element={<AdminShell><AdminSEOPage /></AdminShell>} />
            <Route path="/admin/redirects" element={<AdminShell><AdminRedirectsPage /></AdminShell>} />
            <Route path="/admin/menus" element={<AdminShell><AdminMenusPage /></AdminShell>} />
            <Route path="/admin/users" element={<AdminShell><AdminUsersPage /></AdminShell>} />
            <Route path="/admin/settings" element={<AdminShell><AdminSettingsPage /></AdminShell>} />
            <Route path="/admin/collections" element={<AdminShell><AdminCollectionsPage /></AdminShell>} />
            <Route path="/admin/events" element={<AdminShell><AdminEventsPage /></AdminShell>} />
            <Route path="/admin/stories" element={<AdminShell><AdminStoriesPage /></AdminShell>} />
            <Route path="/admin/ideas" element={<AdminShell><AdminIdeasPage /></AdminShell>} />

            {/* Public - Turkish routes */}
            <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />

            <Route path="/kapadokya-gunlugu" element={<PublicShell><JournalListPage type="journal" title="Kapadokya Günlüğü" overline="Editör Seçkisi" description="Editör kalemiyle yazılmış uzun soluklu yazılar — vadiler, mevsimler, gastronomi, mağara hayatı ve daha fazlası." /></PublicShell>} />
            <Route path="/kapadokya-gunlugu/:slug" element={<PublicShell><ArticleDetailPage section="journal" /></PublicShell>} />

            <Route path="/kapadokya-rehberi" element={<PublicShell><JournalListPage type="guide" title="Kapadokya Rehberi" overline="Pratik Rehber" description="Kapadokya'ya gitmeden önce bilmeniz gereken her şey — ulaşım, mevsimler, balon turları, bölgeler, restoranlar." /></PublicShell>} />
            <Route path="/kapadokya-rehberi/:slug" element={<PublicShell><ArticleDetailPage section="guide" /></PublicShell>} />

            <Route path="/capella-cave" element={<PublicShell><HotelLandingPage /></PublicShell>} />
            <Route path="/kurucu-misafir-listesi" element={<PublicShell><KurucuMisafirPage /></PublicShell>} />

            {/* Phase 1.2: Encyclopedia, Collections, Calendar, Stories */}
            <Route path="/kapadokya-ansiklopedisi" element={<PublicShell><AnsiklopediHubPage /></PublicShell>} />
            <Route path="/kapadokya-ansiklopedisi/:slug" element={<PublicShell><ArticleDetailPage section="encyclopedia" /></PublicShell>} />
            <Route path="/koleksiyonlar" element={<PublicShell><KoleksiyonlarListPage /></PublicShell>} />
            <Route path="/koleksiyonlar/:slug" element={<PublicShell><KoleksiyonDetailPage /></PublicShell>} />
            <Route path="/kapadokya-takvimi" element={<PublicShell><KapadokyaTakvimiPage /></PublicShell>} />
            <Route path="/capella-stories" element={<PublicShell><CapellaStoriesPage /></PublicShell>} />

            <Route path="/iletisim" element={<PublicShell><ContactPage /></PublicShell>} />
            <Route path="/sik-sorulan-sorular" element={<PublicShell><FAQPage /></PublicShell>} />
            <Route path="/arama" element={<PublicShell><SearchPage /></PublicShell>} />
            <Route path="/site-haritasi" element={<PublicShell><SitemapPage /></PublicShell>} />

            <Route path="/gizlilik" element={<PublicShell><LegalPage kind="privacy" /></PublicShell>} />
            <Route path="/kullanim-kosullari" element={<PublicShell><LegalPage kind="terms" /></PublicShell>} />
            <Route path="/kvkk" element={<PublicShell><LegalPage kind="kvkk" /></PublicShell>} />

            {/* Eski URL'lerden Türkçe URL'lere 301 yönlendirme (SPA seviyesinde) */}
            <Route path="/journal" element={<Navigate to="/kapadokya-gunlugu" replace />} />
            <Route path="/journal/:slug" element={<RedirectArticle to="/kapadokya-gunlugu" />} />
            <Route path="/rehber" element={<Navigate to="/kapadokya-rehberi" replace />} />
            <Route path="/rehber/:slug" element={<RedirectArticle to="/kapadokya-rehberi" />} />
            <Route path="/sss" element={<Navigate to="/sik-sorulan-sorular" replace />} />
            <Route path="/sitemap" element={<Navigate to="/site-haritasi" replace />} />

            {/* 404 */}
            <Route path="*" element={<PublicShell><NotFoundPage /></PublicShell>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function RedirectArticle({ to }) {
  const slug = window.location.pathname.split("/").filter(Boolean).pop();
  return <Navigate to={`${to}/${slug}`} replace />;
}

export default App;
